import { useEffect, useState } from 'react'

import { Box, Container, Grid } from '@mui/material'
import { GAME_STEP } from 'constants/enums/GameStepEnum'
import { GAME_TYPE } from 'constants/enums/GameTypeEnum'
import { ToastSeverity } from 'constants/enums/ToastSeverityEnum'
import { BusinessTopicType, TopicType } from 'constants/enums/TopicTypeEnum'
import { SUBMITTED_IDEAS } from 'constants/routes'
import { alertTitleTextMap, defaultErrorMessage } from 'content/alertText'
import { brainstormText } from 'content/brainstormText'
import { EMPTY_ARRAY_CHECK } from 'utils/ideationRegex'
import logger from 'utils/logger'
import { ShowWarningModal, UseBlocker } from 'utils/useBlocker'

import { Game } from 'models/gameModels'

import { useIdeaStreakContext } from 'contexts/IdeaStreakProvider'
import { useModalContext } from 'contexts/ModalProvider'
import { useToastContext } from 'contexts/ToastProvider'

import CustomGameTitleBar from 'components/CustomGameTitleBar/CustomGameTitleBar'
import Hero from 'components/Hero/Hero'
import { IdeationRecommendationModal } from 'components/IdeationRecommendationModal/IdeationRecommendationModal'
import LoadingSpinner from 'components/LoadingSpinner'

import IdeationSection from './components/IdeationSection/IdeationSection'
import InnovationSection from './components/InnovationSection/InnovationSection'
import PromptSection from './components/PromptSection/PromptSection'
import { useGameSession, useScenario } from './hooks'
import { DisruptionType, GameSession, ResponseIndex } from './types'

export const TEST_ID = 'battle-ships'

// ---------------------------------------------------------------------------
// BattleShips — outer shell, waits for session to be ready before mounting
// the inner game so that useScenario initializes with real session data.
// ---------------------------------------------------------------------------
function BattleShips({ game }: { game?: Game }) {
	const { showAlert } = useToastContext()
	const session = useGameSession(game?.gameId, () =>
		showAlert?.(defaultErrorMessage)
	)

	if (!session.isReady) {
		return <LoadingSpinner />
	}

	return <BattleShipsGame session={session} />
}

// ---------------------------------------------------------------------------
// BattleShipsGame — inner component, only mounts once session is ready.
// Each disruption type gets its own useScenario instance.
// Adding a new type = one more useScenario call + wiring to the UI.
// ---------------------------------------------------------------------------
function BattleShipsGame({ session }: { session: GameSession }) {
	const [activeType, setActiveType] = useState<DisruptionType>(
		DisruptionType.Marketplace
	)
	const [submittedCurrentRound, setSubmittedCurrentRound] = useState(false)
	const [ideaPrompt, setIdeaPrompt] = useState(false)
	const [step, setStep] = useState<GAME_STEP>(GAME_STEP.Step1)
	const [termsAgreed, setTermsAgreed] = useState(false)
	const [refreshClicksSinceIdeation, setRefreshClicksSinceIdeation] =
		useState(0)

	const { showModal } = useModalContext()
	const { setIdeaStreakCount } = useIdeaStreakContext()
	const { showAlert } = useToastContext()

	const marketplace = useScenario(DisruptionType.Marketplace, session)
	const future = useScenario(DisruptionType.Future, session)

	const activeScenario =
		activeType === DisruptionType.Marketplace ? marketplace : future

	const generateScenarioDisabled = () =>
		!session.companyName.length ||
		!session.topic ||
		!session.companyProblem.length

	const generateNewScenarioDisabled = () =>
		generateScenarioDisabled() || !activeScenario.scenario.prompt

	const submitIdeasDisabled = () => {
		const competitorResponses =
			activeScenario.scenario.responses[
				ResponseIndex.CompetitorResponse
			] ?? []
		if (
			generateScenarioDisabled() ||
			!activeScenario.scenario.authorIdeation[
				ResponseIndex.CompetitorResponse
			]?.length ||
			competitorResponses.length === 0 ||
			(session.gameId && !termsAgreed)
		)
			return true
		return false
	}

	const fields = [activeScenario.scenario.authorIdeation]
	const blocker = UseBlocker(fields, submittedCurrentRound)

	useEffect(() => {
		setIdeaPrompt(
			activeScenario.scenario.authorIdeation.some(
				(idea) => idea.length >= 8
			) ?? false
		)
	}, [activeScenario.scenario.authorIdeation])

	useEffect(() => {
		if (!submittedCurrentRound) {
			return ShowWarningModal(blocker, showModal)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [blocker])

	useEffect(() => {
		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			if (ideaPrompt) {
				event.preventDefault()
			}
		}
		window.addEventListener('beforeunload', handleBeforeUnload)
		return () =>
			window.removeEventListener('beforeunload', handleBeforeUnload)
	}, [ideaPrompt])

	const reloadIdeas = async (newPrompt: string) => {
		try {
			await activeScenario.loadExamplesForPrompt(newPrompt)
		} catch (error) {
			logger.error(error)
			showAlert?.(defaultErrorMessage)
		}
	}

	const handleDisruptionTypeChange = (newType: DisruptionType) => {
		setActiveType(newType)
		const targetScenario =
			newType === DisruptionType.Marketplace ? marketplace : future
		targetScenario.initializeIfEmpty().catch((error) => {
			logger.error(error)
			showAlert?.(defaultErrorMessage)
		})
	}

	const hasUnsavedIdeation = () =>
		step === GAME_STEP.Step2 &&
		!submittedCurrentRound &&
		activeScenario.scenario.authorIdeation.some(
			(ideation) =>
				ideation.replace(EMPTY_ARRAY_CHECK, '').trim().length > 0
		)

	const refreshPrompt = async () => {
		if (hasUnsavedIdeation()) {
			return ShowWarningModal(
				{
					state: 'blocked',
					location: {
						pathname: '',
						search: '',
						hash: '',
						state: null,
						key: 'kxdgy6c2',
					},
					reset: () => logger.debug('reset'),
					proceed: () => {
						startNewRound().catch((error) => {
							logger.error(error)
							showAlert?.(defaultErrorMessage)
						})
					},
				},
				showModal
			)
		}

		if (refreshClicksSinceIdeation < 2) {
			startNewRound().catch((err) => {
				logger.error(err)
				showAlert?.(defaultErrorMessage)
			})
		}

		setRefreshClicksSinceIdeation(refreshClicksSinceIdeation + 1)
	}

	const startNewRound = async () => {
		setStep(GAME_STEP.Step2)
		window.scrollTo({ top: 0 })
		await activeScenario.startNewRound()
	}

	const handleCreatePrompt = () => {
		startNewRound().catch((error) => {
			logger.error(error)
			showAlert?.(defaultErrorMessage)
		})
	}

	const handleChange = (i: number) => (val: string) => {
		setRefreshClicksSinceIdeation(0)
		activeScenario.setAuthorIdeation(i, val)
	}

	const handleSubmitIdeas = async () => {
		try {
			await activeScenario.submitIdeas()
			setSubmittedCurrentRound(true)
			showAlert?.({ severity: ToastSeverity.SUCCESS })
			setIdeaStreakCount((prevState) => prevState + 1)
		} catch (error) {
			logger.error(error)
			showAlert?.({
				title: alertTitleTextMap.submitIdeaError.title,
				bodyText: alertTitleTextMap.submitIdeaError.text,
				severity: ToastSeverity.ERROR,
			})
			return
		}
		try {
			await startNewRound()
			setSubmittedCurrentRound(false)
		} catch (error) {
			logger.error(error)
			showAlert?.(defaultErrorMessage)
		}
	}

	const handleGenerateCompetitorResponse = async () => {
		try {
			await activeScenario.updateCompetitorResponse()
		} catch (error) {
			logger.error(error)
			showAlert?.(defaultErrorMessage)
		}
	}

	const viewSubmittedIdeas = () => {
		window.open(`${window.location.origin}${SUBMITTED_IDEAS}`, '_blank')
	}

	const onIdeationRecommendationBackClicked = () => {
		setStep(GAME_STEP.Step1)
		onIdeationRecommendationModalClosed()
	}

	const onIdeationRecommendationModalClosed = () => {
		setRefreshClicksSinceIdeation(0)
	}

	const globalLoading =
		marketplace.disruptionIsLoading ||
		future.disruptionIsLoading ||
		marketplace.examplesIsLoading ||
		future.examplesIsLoading

	return (
		<Box>
			<IdeationRecommendationModal
				refreshClicksSinceIdeation={refreshClicksSinceIdeation}
				onBack={onIdeationRecommendationBackClicked}
				onClose={onIdeationRecommendationModalClosed}
			/>
			<Hero
				step={step}
				gameTypeEnum={GAME_TYPE.BATTLE_SHIPS}
				innovationCompany={session.companyName}
				innovationTopic={session.topic as TopicType}
				companyProblem={session.companyProblem}
				isLoading={activeScenario.disruptionIsLoading}
				setInnovationCompany={session.setCompanyName}
				setInnovationTopic={session.setTopic}
				setCompanyProblem={session.setCompanyProblem}
				createPrompt={handleCreatePrompt}
				hideEdit={Boolean(session.gameTitle || session.endTime)}
			/>
			<Container
				data-testid={`${TEST_ID}-container`}
				maxWidth={step === GAME_STEP.Step1 ? 'md' : 'lg'}
				sx={{
					paddingTop: '3rem',
					paddingBottom: '3rem',
					paddingX: '2rem',
					maxWidth: '1440px',
				}}
			>
				{(session.gameTitle || session.endTime) && (
					<CustomGameTitleBar
						game={
							{
								gameTitle: session.gameTitle,
								endTime: session.endTime,
							} as Game
						}
					/>
				)}
				{step === GAME_STEP.Step1 && (
					<InnovationSection
						topic={session.topic as BusinessTopicType}
						companyName={session.companyName}
						setCompanyName={session.setCompanyName}
						companyRisk={session.companyProblem}
						setCompanyRisk={session.setCompanyProblem}
						setTopic={session.setTopic}
						createPrompt={handleCreatePrompt}
						resetAll={session.resetAll}
						generateScenarioDisabled={generateScenarioDisabled}
						isLoading={globalLoading}
						editable={
							session.gameId
								? session.gameTitle === '' &&
									session.endTime === ''
								: true
						}
					/>
				)}
				{step === GAME_STEP.Step2 && (
					<>
						<Grid container>
							<Grid item md={6}>
								<PromptSection
									gameTypeEnum={GAME_TYPE.BATTLE_SHIPS}
									prompt={activeScenario.scenario.prompt}
									currentResponses={
										activeScenario.scenario.responses
									}
									isLoading={
										activeScenario.disruptionIsLoading
									}
									refreshPrompt={refreshPrompt}
									generateNewScenarioDisabled={
										generateNewScenarioDisabled
									}
									reloadIdeas={reloadIdeas}
									disruptionType={activeType}
									onDisruptionTypeChange={
										handleDisruptionTypeChange
									}
								/>
							</Grid>
							<Grid item md={6}>
								<IdeationSection
									activeType={activeType}
									gameTypeEnum={GAME_TYPE.BATTLE_SHIPS}
									isLoading={
										activeScenario.competitorIsLoading
									}
									handleSubmitIdeas={handleSubmitIdeas}
									submitIdeasDisabled={submitIdeasDisabled}
									authorIdeation={
										activeScenario.scenario.authorIdeation
									}
									handleChange={handleChange}
									viewSubmittedIdeas={viewSubmittedIdeas}
									cautionText={
										session.gameTitle || session.endTime
											? brainstormText.global
													.alternateWarningText
											: brainstormText.global.warningText
									}
									termsAgreed={termsAgreed}
									setTermsAgreed={setTermsAgreed}
									gameId={session.gameId ?? undefined}
									generateCompetitorResponse={
										handleGenerateCompetitorResponse
									}
									setCompetitorResponse={
										activeScenario.setCompetitorResponse
									}
									competitorResponses={
										activeScenario.scenario.responses[
											ResponseIndex.CompetitorResponse
										] ?? []
									}
								/>
							</Grid>
						</Grid>
					</>
				)}
			</Container>
		</Box>
	)
}

export default BattleShips
